/**
 * LlmHealthPill — Navbar status indicator for the AI Tutor stack.
 *
 *   🟢 healthy        — chat working normally
 *   🟡 fallback live  — Emergent key OK, Anthropic fallback also set
 *   🟠 limited        — chat works but RAG embeddings are offline
 *   🔴 down           — no chat provider key configured / health probe failed
 *
 * Shown only for faculty/dev/admin — students don't need to see it.
 * Polls /api/chat/llm-health every 60s so a budget exhaustion is visible
 * within a minute, before students notice.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/apiClient';
import useEffectiveRole from '../hooks/useEffectiveRole';

const POLL_MS = 60_000;

const LlmHealthPill = () => {
  const { isFaculty } = useEffectiveRole();
  const [h, setH] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!isFaculty) return;
    let cancelled = false;
    const probe = async () => {
      try {
        const r = await api.get('/api/chat/llm-health');
        if (!cancelled) { setH(r.data); setErr(false); }
      } catch {
        if (!cancelled) { setErr(true); setH(null); }
      }
    };
    probe();
    const t = setInterval(probe, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [isFaculty]);

  if (!isFaculty) return null;

  // ──────────── classify ────────────
  let dot = '⚫'; let label = 'Tutor'; let title = 'AI Tutor status'; let color = 'text-gray-500';
  if (err || !h) {
    dot = '🔴'; label = 'Tutor down'; color = 'text-red-600';
    title = err ? 'Could not reach /api/chat/llm-health' : 'Probing…';
  } else if (!h.emergent_key_set && !h.institution_key_set && !h.anthropic_fallback_set) {
    dot = '🔴'; label = 'No LLM key'; color = 'text-red-600';
    title = 'No chat provider configured. Set EMERGENT_LLM_KEY or ANTHROPIC_API_KEY.';
  } else if (h.anthropic_fallback_set) {
    dot = '🟢'; label = 'Tutor + Claude fallback'; color = 'text-emerald-700';
    title = `Chat: ${h.chat_provider}. Anthropic fallback armed. Embeddings ${h.embeddings_active ? 'active' : 'offline'}.`;
  } else if (!h.embeddings_active) {
    dot = '🟠'; label = 'Tutor (no RAG)'; color = 'text-amber-700';
    title = 'Chat OK, but OpenAI embeddings exhausted — RAG/semantic-cache disabled.';
  } else {
    dot = '🟢'; label = 'Tutor healthy'; color = 'text-emerald-700';
    title = `Chat: ${h.chat_provider}. Embeddings active.`;
  }

  return (
    <Link
      to="/developer"
      title={title}
      className={`hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 border border-gray-200 text-[11px] font-[500] ${color} hover:bg-gray-100`}
      data-testid="llm-health-pill"
    >
      <span className="text-[10px] leading-none">{dot}</span>
      <span>{label}</span>
    </Link>
  );
};

export default LlmHealthPill;
